import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Building2, Save, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { invalidateCmsCache } from '../../hooks/useCmsContent';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FIELDS = [
  { key: 'legal_name', label: 'Legal Business Name *', placeholder: 'Creative Birthdays Inc.' },
  { key: 'trade_name', label: 'Trade Name (DBA)', placeholder: 'Creative Birthdays' },
  { key: 'business_number', label: 'CRA Business Number', placeholder: '123456789RP0001',
    hint: 'Your 9-digit BN + program identifier (e.g. RP0001 for payroll).' },
  { key: 'address_line1', label: 'Address Line 1 *', placeholder: '1500 Upper Middle Road West' },
  { key: 'address_line2', label: 'Address Line 2', placeholder: 'Unit 4' },
  { key: 'city', label: 'City *', placeholder: 'Oakville' },
  { key: 'province', label: 'Province *', placeholder: 'ON' },
  { key: 'postal_code', label: 'Postal Code *', placeholder: 'L6M 3G3' },
  { key: 'country', label: 'Country', placeholder: 'Canada' },
  { key: 'phone', label: 'Phone', placeholder: '905-616-7617' },
  { key: 'email', label: 'Email', placeholder: 'info@creativebirthdays.ca' },
];

export const CrmEmployerSettings = () => {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/cms/content/employer`,
        { withCredentials: true });
      // Endpoint returns the data dict directly (e.g. {legal_name, address_line1, ...})
      setData(r.data || {});
    } catch (e) {
      toast.error('Failed to load employer settings');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const onChange = (key, val) => setData((d) => ({ ...d, [key]: val }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/cms/content/employer`,
        { data }, { withCredentials: true });
      invalidateCmsCache();
      toast.success('Employer settings saved — paystubs will use these on the next download.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm('Restore the bundled defaults? Your current values will be lost.')) return;
    setSaving(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/cms/content/employer/reset`,
        {}, { withCredentials: true });
      setData(r.data || {});
      invalidateCmsCache();
      toast.success('Reset to defaults');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reset');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-[50vh]"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#FF3B6B]" /></div>;

  return (
    <div data-testid="crm-employer-settings">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            <Building2 className="inline w-7 h-7 mr-2 text-[#FF3B6B]" />
            Employer & Payroll Identity
          </h1>
          <p className="text-[#6B6B7A] max-w-2xl">
            Used on Ontario/Canada-compliant paystubs. The legal name and CRA Business Number appear on every employee's PDF for tax-filing purposes.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={reset} className="rounded-full" data-testid="employer-reset-btn">
            <RefreshCw className="w-4 h-4 mr-1" /> Reset to defaults
          </Button>
          <Button onClick={save} disabled={saving} className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white" data-testid="employer-save-btn">
            <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-[#E8E5DF] p-6 max-w-3xl">
        <div className="grid sm:grid-cols-2 gap-4">
          {FIELDS.map((f) => (
            <div key={f.key} className={f.key === 'legal_name' || f.key === 'address_line1' ? 'sm:col-span-2' : ''}>
              <Label className="text-xs text-[#1E1E24]">{f.label}</Label>
              <Input
                value={data[f.key] || ''}
                onChange={(e) => onChange(f.key, e.target.value)}
                placeholder={f.placeholder}
                className="mt-1 bg-white"
                data-testid={`employer-${f.key}`}
              />
              {f.hint && <p className="text-[10px] text-[#6B6B7A] mt-1">{f.hint}</p>}
            </div>
          ))}
        </div>
        <div className="mt-6 rounded-xl bg-[#FFFDF8] border border-[#E8E5DF] p-3 text-xs text-[#6B6B7A]">
          <p className="font-semibold text-[#1E1E24] mb-1">Where these fields appear</p>
          <ul className="list-disc pl-5 space-y-0.5">
            <li><b>Legal name + CRA BN</b> → top-right header of every paystub PDF.</li>
            <li><b>Address + phone + email</b> → employer block on each paystub.</li>
            <li><b>Trade name</b> → large pink heading on the paystub.</li>
            <li>Changes apply to the very next paystub download — no regeneration required.</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default CrmEmployerSettings;
